<template>
  <transition name="fade">
    <div
      v-if="visible"
      class="fixed inset-0 z-[10050] overflow-hidden overscroll-none flex items-center justify-center p-3 md:p-4"
    >
      <div
        class="absolute inset-0 bg-black/40"
        @click="handleClose"
        @wheel.prevent
        @touchmove.prevent
      ></div>

      <div
        class="relative z-[10060] w-full max-w-4xl bg-white rounded-2xl shadow-xl border border-gray-100 flex flex-col h-[85dvh] md:h-auto md:max-h-[90vh] overflow-hidden"
        @click.stop
        @wheel.stop
        @touchmove.stop
      >
        <!-- Header -->
        <div
          class="flex items-center gap-2 sm:gap-3 px-3 sm:px-4 py-2.5 sm:py-3 border-b border-gray-200 flex-shrink-0"
        >
          <div class="min-w-0 flex-shrink-0">
            <h3 class="text-base sm:text-lg font-semibold text-black whitespace-nowrap">
              Couleurs/motifs
            </h3>
            <p v-if="product" class="text-[11px] text-gray-500 truncate max-w-[9rem] sm:max-w-none">
              {{ product.reference }}
            </p>
          </div>
          <div class="relative flex-1 max-w-md ml-auto min-w-0">
            <i
              class="fas fa-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-xs"
            ></i>
            <input
              ref="searchInputRef"
              v-model.trim="searchQuery"
              type="text"
              autocomplete="off"
              placeholder="Rechercher une couleur"
              class="w-full pl-9 pr-9 py-2 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-black focus:border-black"
              role="combobox"
              :aria-expanded="isSearchDropdownOpen"
              @focus="openSearchDropdown"
              @input="openSearchDropdown"
              @blur="closeSearchDropdown"
              @keydown.down.prevent="moveSearchHighlight(1)"
              @keydown.up.prevent="moveSearchHighlight(-1)"
              @keydown.enter.prevent="selectHighlightedSearchColor"
              @keydown.tab.exact.prevent="focusSaveButton"
            />
            <button
              v-if="searchQuery"
              type="button"
              tabindex="-1"
              class="absolute right-2 top-1/2 z-10 -translate-y-1/2 rounded-full p-1 text-gray-400 hover:text-black"
              aria-label="Effacer la recherche"
              @mousedown.prevent
              @click="clearSearch"
            >
              <i class="fas fa-times text-xs"></i>
            </button>
            <div
              v-if="isSearchDropdownOpen && searchQuery"
              class="absolute left-0 right-0 top-full z-50 mt-1 max-h-64 overflow-y-auto rounded-lg border border-gray-300 bg-white shadow-lg"
              role="listbox"
            >
              <p v-if="!searchResults.length" class="px-3 py-3 text-sm text-gray-500">
                Aucune couleur trouvée
              </p>
              <button
                v-for="(color, index) in searchResults"
                :key="`search-${color.id_couleur}`"
                type="button"
                tabindex="-1"
                class="flex w-full items-center gap-2 border-b border-gray-100 px-3 py-2 text-left text-sm last:border-b-0"
                :class="[
                  index === highlightedSearchIndex ? 'bg-blue-100' : 'hover:bg-gray-100',
                  isColorDisabled(color.id_couleur) ? 'opacity-50' : '',
                ]"
                :disabled="isColorDisabled(color.id_couleur)"
                @mousedown.prevent="selectSearchColor(color)"
              >
                <span
                  class="color-swatch-dot h-4 w-4 flex-shrink-0 rounded-full"
                  :style="getColorSwatchStyle(color.label, color.hexColor)"
                ></span>
                <span class="truncate">{{ color.label }}</span>
                <span
                  v-if="isColorDisabled(color.id_couleur)"
                  class="ml-auto flex-shrink-0 text-xs text-gray-500"
                >Utilisée</span>
              </button>
            </div>
          </div>
          <span
            v-if="localSelectedIds.length"
            class="text-[11px] sm:text-xs text-black font-medium whitespace-nowrap flex-shrink-0"
          >
            {{ localSelectedIds.length }} sél.
          </span>
          <button
            type="button"
            class="text-gray-500 hover:text-black p-2 flex-shrink-0"
            aria-label="Fermer"
            @click="handleClose"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Mobile : navigation horizontale -->
        <div
          class="flex md:hidden flex-col border-b border-gray-100 flex-shrink-0 overscroll-contain"
        >
          <template v-if="visibleColorFamilies.length">
            <button
              type="button"
              class="flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide"
              @click="toggleCouleursSection"
            >
              <span>Couleurs</span>
              <i
                class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                :class="{ 'collapse-chevron--open': couleursSectionOpen }"
              ></i>
            </button>
            <div
              class="collapse-panel"
              :class="couleursSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
            >
              <div class="collapse-panel__inner">
                <div class="flex gap-2 px-3 pb-2 overflow-x-auto">
                  <button
                    v-for="family in visibleColorFamilies"
                    :key="'mc-' + family.id"
                    type="button"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors"
                    :class="navButtonClass('couleurs', family.id)"
                    @click="selectColorFamily(family.id)"
                  >
                    <span
                      class="color-swatch-dot w-3.5 h-3.5 rounded-full flex-shrink-0"
                      :style="familySwatchStyle(family)"
                    ></span>
                    {{ family.label }}
                    <span
                      v-if="pendingCountByColorFamily[family.id]"
                      class="opacity-80"
                    >({{ pendingCountByColorFamily[family.id] }})</span>
                  </button>
                </div>
              </div>
            </div>
          </template>
          <template v-if="visibleMotifCategories.length">
            <button
              type="button"
              class="flex items-center justify-between px-3 py-2 text-sm font-bold text-black uppercase tracking-wide"
              :class="visibleColorFamilies.length ? 'border-t border-gray-100' : ''"
              @click="toggleMotifsSection"
            >
              <span>Motifs</span>
              <i
                class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                :class="{ 'collapse-chevron--open': motifsSectionOpen }"
              ></i>
            </button>
            <div
              class="collapse-panel"
              :class="motifsSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
            >
              <div class="collapse-panel__inner">
                <div class="flex gap-2 px-3 pb-2 overflow-x-auto">
                  <button
                    v-for="category in visibleMotifCategories"
                    :key="'mm-' + category.id"
                    type="button"
                    class="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full text-xs whitespace-nowrap border flex-shrink-0 transition-colors"
                    :class="navButtonClass('motifs', category.id)"
                    @click="selectMotifCategory(category.id)"
                  >
                    {{ category.label }}
                    <span
                      v-if="pendingCountByMotifCategory[category.id]"
                      class="opacity-80"
                    >({{ pendingCountByMotifCategory[category.id] }})</span>
                  </button>
                </div>
              </div>
            </div>
          </template>
        </div>

        <div class="flex flex-1 min-h-0 overflow-hidden">
          <!-- Desktop : sidebar -->
          <aside
            class="hidden md:block w-44 lg:w-48 flex-shrink-0 bg-gray-50 border-r border-gray-200 overflow-y-auto overscroll-contain"
          >
            <template v-if="visibleColorFamilies.length">
              <button
                type="button"
                class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors"
                @click="toggleCouleursSection"
              >
                <span>Couleurs</span>
                <i
                  class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                  :class="{ 'collapse-chevron--open': couleursSectionOpen }"
                ></i>
              </button>
              <div
                class="collapse-panel"
                :class="couleursSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
              >
                <div class="collapse-panel__inner collapse-panel__inner--sidebar">
                  <button
                    v-for="family in visibleColorFamilies"
                    :key="family.id"
                    type="button"
                    class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors"
                    :class="sidebarItemClass('couleurs', family.id)"
                    @click="selectColorFamily(family.id)"
                  >
                    <span
                      class="color-swatch-dot w-5 h-5 rounded-full flex-shrink-0"
                      :style="familySwatchStyle(family)"
                    ></span>
                    <span class="truncate flex-1">{{ family.label }}</span>
                    <span
                      v-if="pendingCountByColorFamily[family.id]"
                      class="text-xs text-gray-500"
                    >
                      ({{ pendingCountByColorFamily[family.id] }})
                    </span>
                  </button>
                </div>
              </div>
            </template>

            <template v-if="visibleMotifCategories.length">
              <button
                type="button"
                class="w-full flex items-center justify-between px-3 py-2.5 text-sm font-bold text-black uppercase tracking-wide hover:bg-white/70 transition-colors"
                :class="visibleColorFamilies.length ? 'border-t border-gray-200' : ''"
                @click="toggleMotifsSection"
              >
                <span>Motifs</span>
                <i
                  class="fas fa-chevron-down collapse-chevron text-[10px] text-gray-400"
                  :class="{ 'collapse-chevron--open': motifsSectionOpen }"
                ></i>
              </button>
              <div
                class="collapse-panel"
                :class="motifsSectionOpen ? 'collapse-panel--open' : 'collapse-panel--closed'"
              >
                <div class="collapse-panel__inner collapse-panel__inner--sidebar">
                  <button
                    v-for="category in visibleMotifCategories"
                    :key="category.id"
                    type="button"
                    class="w-full flex items-center gap-2.5 px-3 py-2.5 text-sm text-left transition-colors"
                    :class="sidebarItemClass('motifs', category.id)"
                    @click="selectMotifCategory(category.id)"
                  >
                    <span class="truncate flex-1">{{ category.label }}</span>
                    <span
                      v-if="pendingCountByMotifCategory[category.id]"
                      class="text-xs text-gray-500"
                    >
                      ({{ pendingCountByMotifCategory[category.id] }})
                    </span>
                  </button>
                </div>
              </div>
            </template>
          </aside>

          <!-- Nuances + quantités -->
          <section
            ref="scrollSectionRef"
            class="flex-1 overflow-y-auto overscroll-contain p-3 sm:p-5 min-h-0"
          >
            <div
              v-if="saveError && !showQuantitesParTaille"
              class="mb-3 p-2.5 bg-red-50 border border-red-300 rounded-lg text-xs text-red-700 font-medium"
            >
              <i class="fas fa-exclamation-circle mr-1"></i>{{ saveError }}
            </div>

            <div
              v-if="showQuantitesParTaille"
              id="qty-anchor"
              ref="qtyAnchorRef"
              class="scroll-mt-2"
            >
              <div
                v-if="saveError"
                class="mb-3 p-2.5 bg-red-50 border border-red-300 rounded-lg text-xs text-red-700 font-medium"
              >
                <i class="fas fa-exclamation-circle mr-1"></i>{{ saveError }}
              </div>
              <div
                class="mb-3 rounded-lg border border-blue-200 bg-blue-50/80 p-2.5 text-xs text-blue-800"
              >
                <i class="fas fa-info-circle mr-1"></i>
                Couleurs mélangées : définissez les quantités par taille pour chaque couleur sélectionnée.
              </div>
            </div>

            <!-- Sélectionnées + qty (mélangées) -->
            <div
              v-if="showQuantitesParTaille && selectedColors.length"
              class="mb-4 space-y-2"
            >
              <div class="text-xs font-semibold text-gray-500 uppercase tracking-wide">
                Sélectionnées — quantités
              </div>
              <div
                v-for="color in selectedColors"
                :id="'qty-color-' + color.id_couleur"
                :key="'qty-' + color.id_couleur"
                class="rounded-lg border bg-white p-2.5 scroll-mt-2 transition-colors"
                :class="
                  qtyErrorColorId === color.id_couleur
                    ? 'border-red-400 ring-1 ring-red-400'
                    : 'border-gray-200'
                "
              >
                <div class="flex items-center justify-between gap-2 mb-2">
                  <span class="text-sm font-medium text-black truncate">{{ color.label }}</span>
                  <button
                    type="button"
                    class="text-xs text-gray-400 hover:text-black"
                    @click="toggleColor(color.id_couleur)"
                  >
                    ✕
                  </button>
                </div>
                <div
                  v-if="taillesList.length"
                  class="grid grid-cols-2 sm:grid-cols-3 gap-2"
                >
                  <div
                    v-for="(taille, tailleIndex) in taillesList"
                    :key="tailleIndex"
                    class="flex items-center gap-2"
                  >
                    <label class="text-xs text-black min-w-[2rem]">{{ taille }}</label>
                    <input
                      type="number"
                      :value="getQuantiteParTaille(color.id_couleur, tailleIndex)"
                      min="0"
                      placeholder="0"
                      class="w-full px-2 py-1 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-black text-center"
                      @input="updateQuantiteParTaille(color.id_couleur, tailleIndex, $event)"
                    />
                  </div>
                </div>
              </div>
            </div>

            <h4
              v-if="panelTitle"
              class="text-sm font-medium text-black mb-3 capitalize"
            >
              {{ panelTitle }}
            </h4>

            <div
              v-if="displayedShades.length === 0"
              class="text-sm text-gray-500 py-8 text-center"
            >
              {{
                searchQuery
                  ? "Aucune couleur trouvée"
                  : activeSection === "motifs"
                    ? "Aucun motif dans cette famille"
                    : "Aucune couleur dans cette famille"
              }}
            </div>

            <div
              v-else
              class="grid grid-cols-2 sm:grid-cols-3 gap-x-3 gap-y-4 sm:gap-x-6 sm:gap-y-5"
            >
              <button
                v-for="color in displayedShades"
                :key="color.id_couleur"
                type="button"
                class="group text-left focus:outline-none"
                :class="[
                  isColorDisabled(color.id_couleur)
                    ? 'opacity-40 cursor-not-allowed'
                    : '',
                  activeSection === 'motifs'
                    ? localSelectedIds.includes(color.id_couleur)
                      ? 'rounded-md border border-black ring-1 ring-black px-2.5 py-2'
                      : 'rounded-md border border-gray-200 px-2.5 py-2 hover:border-gray-400'
                    : '',
                ]"
                :title="
                  isColorDisabled(color.id_couleur)
                    ? 'Cette couleur est déjà utilisée par un autre produit de la même liaison'
                    : ''
                "
                :disabled="isColorDisabled(color.id_couleur)"
                @click="toggleColor(color.id_couleur)"
              >
                <div
                  class="text-xs sm:text-sm text-black truncate flex items-center justify-between gap-2"
                  :class="activeSection === 'motifs' ? '' : 'mb-1.5'"
                >
                  <span class="truncate">
                    {{ color.label }}
                    <span
                      v-if="isColorDisabled(color.id_couleur)"
                      class="text-[10px] text-gray-500"
                    >(utilisée)</span>
                  </span>
                  <i
                    v-if="
                      activeSection === 'motifs' &&
                      localSelectedIds.includes(color.id_couleur)
                    "
                    class="fas fa-check text-[10px] flex-shrink-0"
                  ></i>
                </div>
                <div
                  v-if="activeSection !== 'motifs'"
                  class="relative w-full h-12 sm:h-16 rounded-md border overflow-hidden transition-all"
                  :class="
                    localSelectedIds.includes(color.id_couleur)
                      ? 'border-black ring-1 ring-black'
                      : 'border-gray-200 group-hover:border-gray-400'
                  "
                  :style="getColorSwatchStyle(color.label, color.hexColor)"
                >
                  <div
                    v-if="localSelectedIds.includes(color.id_couleur)"
                    class="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-black text-white flex items-center justify-center shadow"
                  >
                    <i class="fas fa-check text-[8px]"></i>
                  </div>
                </div>
              </button>
            </div>
          </section>
        </div>

        <!-- Footer -->
        <div class="border-t border-gray-200 bg-white px-3 sm:px-4 py-3 flex-shrink-0">
          <div class="flex items-center justify-between gap-2">
            <button
              type="button"
              class="px-3 sm:px-4 py-2 text-sm border border-teal-600 text-teal-700 rounded-lg hover:bg-teal-50 transition-colors flex items-center gap-2"
              @click="handleAddColor"
            >
              <i
                :class="[
                  'fas text-xs',
                  venduPar === 'melangees' ? 'fa-camera' : 'fa-palette',
                ]"
              ></i>
              <span>{{
                venduPar === "melangees" ? "Nouvelle photo" : "Nouvelle couleur"
              }}</span>
            </button>
            <div class="flex gap-2">
              <button
                type="button"
                class="px-4 py-2 text-sm border border-gray-800 rounded-lg hover:bg-gray-50 transition-colors"
                @click="handleClose"
              >
                Annuler
              </button>
              <button
                ref="saveButtonRef"
                type="button"
                class="px-4 py-2 text-sm bg-black text-white rounded-lg hover:bg-gray-800 transition-colors"
                @click="handleSave"
                @keydown.tab.exact.prevent="focusSearchInput"
              >
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, nextTick, ref, watch, onBeforeUnmount } from "vue";
import type { ProduitCatalogue } from "@/types";
import type { CouleurAPI } from "@/types/mel-components";
import {
  type BasicColorFamily,
  type BasicColorFamilyId,
  getColorSwatchStyle,
  getFamiliesWithVendorColors,
  groupVendorColorsByBasicFamily,
} from "@/utils/colorFamilies";
import {
  type MotifCategory,
  formatMotifPanelTitle,
  getMotifCategoriesWithColors,
  groupMotifsByCategory,
  splitSolidAndMotifColors,
} from "@/utils/motifFamilies";

type PickerSection = "couleurs" | "motifs";

export interface ColorOption {
  id_couleur: number;
  label: string;
  hexColor?: string | null;
  motifCategorie?: string | null;
  idCouleurEfashion?: number | null;
  officiel?: number | null;
}

interface ColorWithQuantities {
  id_couleur: number;
  quantitesParTaille?: number[];
}

interface Props {
  visible: boolean;
  product: ProduitCatalogue | null;
  colors: ColorOption[];
  selectedIds: number[];
  taillesList?: string[];
  venduPar?: string;
  colorsWithQuantities?: ColorWithQuantities[];
  disabledColorIds?: number[];
}

interface Emits {
  (e: "update:visible", value: boolean): void;
  (e: "add-color"): void;
  (
    e: "save",
    payload: {
      productId: number;
      selectedIds: number[];
      colorsWithQuantities?: ColorWithQuantities[];
    },
  ): void;
}

const props = defineProps<Props>();
const emit = defineEmits<Emits>();

const localSelectedIds = ref<number[]>([]);
const localColorsWithQuantities = ref<Map<number, number[]>>(new Map());
const searchQuery = ref("");
const searchInputRef = ref<HTMLInputElement | null>(null);
const saveButtonRef = ref<HTMLButtonElement | null>(null);
const isSearchDropdownOpen = ref(false);
const highlightedSearchIndex = ref(-1);
const activeSection = ref<PickerSection>("couleurs");
const activeFamilyId = ref<BasicColorFamilyId>("beige");
const activeMotifCategory = ref<MotifCategory>("Animaux");
const couleursSectionOpen = ref(true);
const motifsSectionOpen = ref(false);
const saveError = ref<string | null>(null);
const qtyErrorColorId = ref<number | null>(null);
const scrollSectionRef = ref<HTMLElement | null>(null);
const qtyAnchorRef = ref<HTMLElement | null>(null);

const scrollToQuantites = async (colorId?: number | null) => {
  await nextTick();
  const section = scrollSectionRef.value;
  if (!section) return;

  const target =
    (colorId != null
      ? document.getElementById(`qty-color-${colorId}`)
      : null) || qtyAnchorRef.value;

  if (target && section.contains(target)) {
    const targetRect = target.getBoundingClientRect();
    const sectionRect = section.getBoundingClientRect();
    const nextTop =
      section.scrollTop + (targetRect.top - sectionRect.top) - 8;
    section.scrollTo({ top: Math.max(0, nextTop), behavior: "smooth" });
  } else {
    section.scrollTo({ top: 0, behavior: "smooth" });
  }
};

const taillesList = computed(() => props.taillesList || []);

const showQuantitesParTaille = computed(
  () =>
    props.venduPar === "melangees" &&
    taillesList.value &&
    taillesList.value.length > 0,
);

const isSingleSelect = computed(
  () => props.venduPar === "couleurs" || props.venduPar === "tailles",
);

const disabledIds = computed(() =>
  (props.disabledColorIds || [])
    .map((id) => Number(id))
    .filter((n) => !Number.isNaN(n)),
);

const isColorDisabled = (id: number | string) =>
  disabledIds.value.includes(Number(id));

const colorsAsApi = computed<CouleurAPI[]>(() =>
  props.colors.map((c) => ({
    id_couleur: c.id_couleur,
    couleur_FR: c.label,
    couleur_EN: null,
    hexColor: c.hexColor ?? null,
    motifCategorie: c.motifCategorie ?? null,
    idCouleurEfashion: c.idCouleurEfashion ?? null,
    officiel: c.officiel ?? 1,
  })),
);

const solidColorsRef = computed(
  () => splitSolidAndMotifColors(colorsAsApi.value).solid,
);
const motifColorsRef = computed(
  () => splitSolidAndMotifColors(colorsAsApi.value).motifs,
);

const colorGroups = computed(() =>
  groupVendorColorsByBasicFamily(solidColorsRef.value),
);

const motifGroups = computed(() => groupMotifsByCategory(motifColorsRef.value));

const normalize = (s: string) =>
  (s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/\p{Diacritic}+/gu, "");

const matchesSearch = (color: CouleurAPI, term: string) =>
  normalize(color.couleur_FR || "").includes(term);

const searchResults = computed(() => {
  const term = normalize(searchQuery.value);
  if (!term) return [];
  return props.colors.filter(
    (color) =>
      !localSelectedIds.value.includes(color.id_couleur) &&
      normalize(color.label).includes(term),
  );
});

const visibleColorFamilies = computed(() => {
  if (searchQuery.value) {
    const term = normalize(searchQuery.value);
    return getFamiliesWithVendorColors(solidColorsRef.value).filter((family) =>
      (colorGroups.value[family.id] || []).some((color) =>
        matchesSearch(color, term),
      ),
    );
  }
  return getFamiliesWithVendorColors(solidColorsRef.value);
});

const visibleMotifCategories = computed(() => {
  if (searchQuery.value) {
    const term = normalize(searchQuery.value);
    return getMotifCategoriesWithColors(motifColorsRef.value).filter(
      (category) =>
        (motifGroups.value[category.id] || []).some((color) =>
          matchesSearch(color, term),
        ),
    );
  }
  return getMotifCategoriesWithColors(motifColorsRef.value);
});

const displayedShades = computed(() => {
  const term = searchQuery.value ? normalize(searchQuery.value) : "";

  if (activeSection.value === "motifs") {
    let list = (motifGroups.value[activeMotifCategory.value] || []).map(
      (color) => ({
        id_couleur: Number(color.id_couleur),
        label: color.couleur_FR || "",
        hexColor: color.hexColor ?? null,
      }),
    );
    if (term) list = list.filter((color) => normalize(color.label).includes(term));
    return list;
  }

  let list = (colorGroups.value[activeFamilyId.value] || []).map((color) => ({
    id_couleur: Number(color.id_couleur),
    label: color.couleur_FR || "",
    hexColor: color.hexColor ?? null,
  }));
  if (term) list = list.filter((color) => normalize(color.label).includes(term));
  return list;
});

const panelTitle = computed(() => {
  if (activeSection.value === "motifs") {
    return formatMotifPanelTitle(activeMotifCategory.value);
  }
  const family = visibleColorFamilies.value.find(
    (item) => item.id === activeFamilyId.value,
  );
  return family ? `Nuances de ${family.label.toLowerCase()}` : "";
});

const pendingCountByColorFamily = computed(() => {
  const counts: Partial<Record<BasicColorFamilyId, number>> = {};
  for (const family of visibleColorFamilies.value) {
    const count = (colorGroups.value[family.id] || []).filter((color) =>
      localSelectedIds.value.includes(Number(color.id_couleur)),
    ).length;
    if (count > 0) counts[family.id] = count;
  }
  return counts;
});

const pendingCountByMotifCategory = computed(() => {
  const counts: Partial<Record<MotifCategory, number>> = {};
  for (const category of visibleMotifCategories.value) {
    const count = (motifGroups.value[category.id] || []).filter((color) =>
      localSelectedIds.value.includes(Number(color.id_couleur)),
    ).length;
    if (count > 0) counts[category.id] = count;
  }
  return counts;
});

const isActiveNav = (section: PickerSection, id: string) =>
  activeSection.value === section &&
  (section === "couleurs"
    ? activeFamilyId.value === id
    : activeMotifCategory.value === id);

const navButtonClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-black text-white border-black"
    : "bg-white text-black border-gray-300";

const sidebarItemClass = (section: PickerSection, id: string) =>
  isActiveNav(section, id)
    ? "bg-white text-black font-medium shadow-sm"
    : "text-gray-700 hover:bg-white/70";

const selectColorFamily = (familyId: BasicColorFamilyId) => {
  activeSection.value = "couleurs";
  activeFamilyId.value = familyId;
  couleursSectionOpen.value = true;
  motifsSectionOpen.value = false;
};

const selectMotifCategory = (category: MotifCategory) => {
  activeSection.value = "motifs";
  activeMotifCategory.value = category;
  motifsSectionOpen.value = true;
  couleursSectionOpen.value = false;
};

const toggleCouleursSection = () => {
  const willOpen = !couleursSectionOpen.value;
  couleursSectionOpen.value = willOpen;
  if (willOpen) motifsSectionOpen.value = false;
};

const toggleMotifsSection = () => {
  const willOpen = !motifsSectionOpen.value;
  motifsSectionOpen.value = willOpen;
  if (willOpen) couleursSectionOpen.value = false;
};

const familySwatchStyle = (family: BasicColorFamily) => {
  if (family.swatch.startsWith("linear-gradient")) {
    return { backgroundImage: family.swatch };
  }
  return { backgroundColor: family.swatch };
};

const initPickerNavigation = () => {
  activeSection.value = "couleurs";
  activeFamilyId.value =
    getFamiliesWithVendorColors(solidColorsRef.value)[0]?.id || "beige";
  activeMotifCategory.value =
    getMotifCategoriesWithColors(motifColorsRef.value)[0]?.id || "Animaux";

  const solidFamilies = getFamiliesWithVendorColors(solidColorsRef.value);
  const motifCategories = getMotifCategoriesWithColors(motifColorsRef.value);

  if (solidFamilies.length > 0) {
    activeSection.value = "couleurs";
    activeFamilyId.value = solidFamilies[0].id;
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
  } else if (motifCategories.length > 0) {
    activeSection.value = "motifs";
    activeMotifCategory.value = motifCategories[0].id;
    couleursSectionOpen.value = false;
    motifsSectionOpen.value = true;
  }
};

const selectedColors = computed(() =>
  props.colors.filter((c) => localSelectedIds.value.includes(c.id_couleur)),
);

watch(
  () => props.visible,
  (val) => {
    document.body.style.overflow = val ? "hidden" : "";
    if (val) {
      searchQuery.value = "";
      saveError.value = null;
      qtyErrorColorId.value = null;
      initPickerNavigation();
      void nextTick(() => searchInputRef.value?.focus());
    }
  },
  { immediate: true },
);

onBeforeUnmount(() => {
  document.body.style.overflow = "";
});

watch(
  () => props.selectedIds,
  (val) => {
    localSelectedIds.value = [...(val ?? [])];
  },
  { immediate: true },
);

watch(
  () => props.colorsWithQuantities,
  (val) => {
    if (val && Array.isArray(val)) {
      localColorsWithQuantities.value = new Map();
      val.forEach((c) => {
        if (c.quantitesParTaille) {
          localColorsWithQuantities.value.set(c.id_couleur, [
            ...c.quantitesParTaille,
          ]);
        }
      });
    }
  },
  { immediate: true, deep: true },
);

watch(
  () => [showQuantitesParTaille.value, props.taillesList] as const,
  ([shouldShow, tailles]) => {
    if (shouldShow && tailles && tailles.length > 0) {
      localSelectedIds.value.forEach((id) => {
        if (!localColorsWithQuantities.value.has(id)) {
          localColorsWithQuantities.value.set(
            id,
            new Array(tailles.length).fill(0),
          );
        } else {
          const currentQuantites =
            localColorsWithQuantities.value.get(id) || [];
          if (currentQuantites.length !== tailles.length) {
            const newQuantites = new Array(tailles.length).fill(0);
            currentQuantites.forEach((q, i) => {
              if (i < tailles.length) newQuantites[i] = q;
            });
            localColorsWithQuantities.value.set(id, newQuantites);
          }
        }
      });
    }
  },
  { immediate: true, deep: true },
);

watch(
  () => localSelectedIds.value,
  (newIds) => {
    if (
      showQuantitesParTaille.value &&
      taillesList.value &&
      taillesList.value.length > 0
    ) {
      newIds.forEach((id) => {
        if (!localColorsWithQuantities.value.has(id)) {
          localColorsWithQuantities.value.set(
            id,
            new Array(taillesList.value.length).fill(0),
          );
        }
      });
    }
  },
  { deep: true },
);

watch([visibleColorFamilies, visibleMotifCategories], ([families, categories]) => {
  if (
    activeSection.value === "couleurs" &&
    !families.find((family) => family.id === activeFamilyId.value)
  ) {
    if (families[0]) {
      activeFamilyId.value = families[0].id;
    } else if (categories[0]) {
      activeSection.value = "motifs";
      activeMotifCategory.value = categories[0].id;
      motifsSectionOpen.value = true;
      couleursSectionOpen.value = false;
    }
  }

  if (
    activeSection.value === "motifs" &&
    !categories.find((category) => category.id === activeMotifCategory.value)
  ) {
    if (categories[0]) {
      activeMotifCategory.value = categories[0].id;
    } else if (families[0]) {
      activeSection.value = "couleurs";
      activeFamilyId.value = families[0].id;
      couleursSectionOpen.value = true;
      motifsSectionOpen.value = false;
    }
  }
});

watch(searchQuery, () => {
  if (!searchQuery.value) return;
  if (visibleColorFamilies.value[0]) {
    activeSection.value = "couleurs";
    activeFamilyId.value = visibleColorFamilies.value[0].id;
    couleursSectionOpen.value = true;
    motifsSectionOpen.value = false;
    return;
  }
  if (visibleMotifCategories.value[0]) {
    activeSection.value = "motifs";
    activeMotifCategory.value = visibleMotifCategories.value[0].id;
    motifsSectionOpen.value = true;
    couleursSectionOpen.value = false;
  }
});

const toggleColor = (id: number) => {
  if (isColorDisabled(id)) return;

  const alreadySelected = localSelectedIds.value.includes(id);

  if (alreadySelected) {
    localSelectedIds.value = localSelectedIds.value.filter((x) => x !== id);
    localColorsWithQuantities.value.delete(id);
    saveError.value = null;
    return;
  }

  saveError.value = null;

  if (isSingleSelect.value) {
    localSelectedIds.value.forEach((oldId) =>
      localColorsWithQuantities.value.delete(oldId),
    );
    localSelectedIds.value = [id];
  } else {
    localSelectedIds.value = [...localSelectedIds.value, id];
  }

  if (
    showQuantitesParTaille.value &&
    taillesList.value &&
    taillesList.value.length > 0
  ) {
    localColorsWithQuantities.value.set(
      id,
      new Array(taillesList.value.length).fill(0),
    );
  }
};

const showSearchColorPage = (colorId: number) => {
  const motifCategory = visibleMotifCategories.value.find((category) =>
    (motifGroups.value[category.id] || []).some(
      (color) => Number(color.id_couleur) === colorId,
    ),
  );
  if (motifCategory) {
    selectMotifCategory(motifCategory.id);
    return;
  }
  const family = visibleColorFamilies.value.find((item) =>
    (colorGroups.value[item.id] || []).some(
      (color) => Number(color.id_couleur) === colorId,
    ),
  );
  if (family) selectColorFamily(family.id);
};

const selectSearchColor = (color: ColorOption) => {
  showSearchColorPage(color.id_couleur);
  toggleColor(color.id_couleur);
  clearSearch();
};

const clearSearch = () => {
  searchQuery.value = "";
  isSearchDropdownOpen.value = false;
  highlightedSearchIndex.value = -1;
  void nextTick(() => searchInputRef.value?.focus());
};

const openSearchDropdown = (event?: Event) => {
  const value =
    event?.target instanceof HTMLInputElement
      ? event.target.value
      : searchQuery.value;
  isSearchDropdownOpen.value = Boolean(value.trim());
};

const closeSearchDropdown = () => {
  isSearchDropdownOpen.value = false;
};

const moveSearchHighlight = (delta: number) => {
  isSearchDropdownOpen.value = true;
  const length = searchResults.value.length;
  if (!length) return;
  if (highlightedSearchIndex.value < 0) {
    highlightedSearchIndex.value = delta > 0 ? 0 : length - 1;
  } else {
    highlightedSearchIndex.value =
      (highlightedSearchIndex.value + delta + length) % length;
  }
};

const selectHighlightedSearchColor = () => {
  const color =
    searchResults.value[
      highlightedSearchIndex.value >= 0 ? highlightedSearchIndex.value : 0
    ];
  if (color && !isColorDisabled(color.id_couleur)) selectSearchColor(color);
};

const focusSaveButton = () => {
  closeSearchDropdown();
  saveButtonRef.value?.focus();
};

const focusSearchInput = () => {
  searchInputRef.value?.focus();
};

watch(searchQuery, () => {
  highlightedSearchIndex.value = -1;
});

const getQuantiteParTaille = (idCouleur: number, tailleIndex: number): number => {
  const quantites = localColorsWithQuantities.value.get(idCouleur);
  if (!quantites || !Array.isArray(quantites)) return 0;
  return quantites[tailleIndex] || 0;
};

const updateQuantiteParTaille = (
  idCouleur: number,
  tailleIndex: number,
  event: Event,
) => {
  const target = event.target as HTMLInputElement;
  const value = parseInt(target.value) || 0;

  if (value < 0) {
    target.value = "0";
    return;
  }

  if (!localColorsWithQuantities.value.has(idCouleur)) {
    localColorsWithQuantities.value.set(
      idCouleur,
      new Array(taillesList.value?.length || 0).fill(0),
    );
  }

  const quantites = localColorsWithQuantities.value.get(idCouleur)!;
  quantites[tailleIndex] = value;
  localColorsWithQuantities.value.set(idCouleur, [...quantites]);

  if (qtyErrorColorId.value === idCouleur) {
    qtyErrorColorId.value = null;
    saveError.value = null;
  }
};

const handleClose = () => {
  emit("update:visible", false);
};

const handleAddColor = () => {
  emit("add-color");
};

const handleSave = async () => {
  if (!props.product) return;

  saveError.value = null;
  qtyErrorColorId.value = null;

  if (localSelectedIds.value.length === 0) {
    saveError.value = "Veuillez sélectionner au moins une couleur.";
    return;
  }

  const colorsWithQuantities: ColorWithQuantities[] = [];
  if (showQuantitesParTaille.value) {
    const originalIds = new Set(props.selectedIds || []);
    const newlyAddedIds = localSelectedIds.value.filter(
      (id) => !originalIds.has(id),
    );

    localSelectedIds.value.forEach((id) => {
      const quantites = localColorsWithQuantities.value.get(id);
      if (quantites) {
        colorsWithQuantities.push({
          id_couleur: id,
          quantitesParTaille: [...quantites],
        });
      }
    });

    for (const newId of newlyAddedIds) {
      const quantites = localColorsWithQuantities.value.get(newId);
      const total = quantites
        ? quantites.reduce((sum, q) => sum + (q || 0), 0)
        : 0;
      if (total === 0) {
        const color = props.colors.find((c) => c.id_couleur === newId);
        saveError.value = `La couleur "${color?.label || newId}" doit avoir un stock supérieur à 0.`;
        qtyErrorColorId.value = newId;
        await scrollToQuantites(newId);
        return;
      }
    }
  }

  emit("save", {
    productId: props.product.id,
    selectedIds: localSelectedIds.value,
    colorsWithQuantities:
      colorsWithQuantities.length > 0 ? colorsWithQuantities : undefined,
  });
  emit("update:visible", false);
};
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.collapse-panel {
  display: grid;
  transition:
    grid-template-rows 0.28s ease,
    opacity 0.22s ease;
}

.collapse-panel--open {
  grid-template-rows: 1fr;
  opacity: 1;
}

.collapse-panel--closed {
  grid-template-rows: 0fr;
  opacity: 0.72;
}

.collapse-panel__inner {
  overflow: hidden;
  min-height: 0;
}

.collapse-panel__inner--sidebar > button {
  animation: collapse-item-in 0.24s ease both;
}

.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(1) {
  animation-delay: 0.02s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(2) {
  animation-delay: 0.04s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(3) {
  animation-delay: 0.06s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(4) {
  animation-delay: 0.08s;
}
.collapse-panel--open .collapse-panel__inner--sidebar > button:nth-child(n + 5) {
  animation-delay: 0.1s;
}

.collapse-chevron {
  transition: transform 0.28s ease;
}

.collapse-chevron--open {
  transform: rotate(180deg);
}

@keyframes collapse-item-in {
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

/* Pastilles rondes nettes : anneau via box-shadow (évite le crénelage du border) */
.color-swatch-dot {
  display: inline-block;
  border-radius: 50%;
  box-shadow: inset 0 0 0 1px rgba(0, 0, 0, 0.18);
  background-repeat: no-repeat;
  background-size: 100% 100%;
  background-clip: padding-box;
}
</style>
